import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Component/Sidebar';
import {
  fetchEmployeeThunk,
  createEmployeeThunk,
  updateEmployeeThunk
} from '../features/employees/employeeThunks';
import { clearCurrentEmployee } from '../features/employees/employeeSlice';

const EmployeeManagementForm = () => {
  // Redux hooks and navigation
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const isEditing = Boolean(employeeId);

  // Redux state
  const { currentEmployee, loading, error } = useSelector(state => state.employees);
  const user = useSelector(state => state.auth.user);
  
  // Helper function to check if user is admin
  const isAdmin = (user) => {
    if (!user || !user.employee_profile) return false;
    return user.employee_profile.role === 'ADMIN';
  };
  
  const userIsAdmin = isAdmin(user);

  // Modal state for employee creation confirmation
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [creationResponse, setCreationResponse] = useState(null);

  // File upload refs
  const profileImageRef = useRef(null);
  const aadhaarCardRef = useRef(null);
  const panCardRef = useRef(null);
  const qualificationDocRef = useRef(null);
  const resumeRef = useRef(null);
  const employmentDocRef = useRef(null);
  const bankPassbookRef = useRef(null);
  const passportIdRef = useRef(null);
  const spousePhotoIdRef = useRef(null);
  const pccRef = useRef(null);
  const codeOfConductRef = useRef(null);
  const companyAssetsRef = useRef(null);

  // Form state initialization
  const [formData, setFormData] = useState({
    // User information
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    
    // Profile information
    image: null,
    image_preview: null,
    father_name: '',
    mother_name: '',
    official_email: '',
    date_of_birth: '',
    education: '',
    aadhaar_number: '',
    pan_number: '',
    contact_no: '',
    current_address: '',
    permanent_address: '',
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_phone: '',
    
    // Employment information
    role: '',
    department: '',
    shift: '',
    date_of_joining: '',
    allocated_under: '',
    biometric_id: '',
    week_off: '',
    
    // Financial information
    epfo_no: '',
    esic_no: '',
    reference_by: '',
    name_on_passbook: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: '',
    
    // Document uploads
    aadhaar_card: null,
    aadhaar_card_preview: null,
    pan_card: null,
    pan_card_preview: null,
    qualification_document: null,
    qualification_document_preview: null,
    resume: null,
    resume_preview: null,
    employment_document: null,
    employment_document_preview: null,
    bank_passbook: null,
    bank_passbook_preview: null,
    passport_id: null,
    passport_id_preview: null,
    spouse_photo_id: null,
    spouse_photo_id_preview: null,
    pcc: null,
    pcc_preview: null,
    code_of_conduct: null,
    code_of_conduct_preview: null,
    company_assets: null,
    company_assets_preview: null,
  });

  const [formErrors, setFormErrors] = useState({});

  // Helper function to check if file is an image
  const isImageFile = (url) => {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Helper function to get file name from URL
  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'Document';
  };

  // Helper function to render file preview
  const renderFilePreview = (previewUrl, fileName, onDelete, isClickable = true) => {
    if (!previewUrl) return null;

    const isImage = isImageFile(previewUrl);
    const displayName = fileName || getFileNameFromUrl(previewUrl);

    return (
      <div className="incident-report-file-list">
        <ul>
          <li className="incident-report-file-item">
            {isImage ? (
              <img
                src={previewUrl}
                alt={displayName}
                className="incident-report-file-preview"
                onClick={isClickable ? () => window.open(previewUrl, '_blank') : undefined}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
              />
            ) : (
              <div 
                className="incident-report-file-icon"
                onClick={isClickable ? () => window.open(previewUrl, '_blank') : undefined}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
            <span 
              className="incident-report-file-name"
              onClick={isClickable ? () => window.open(previewUrl, '_blank') : undefined}
              style={{ cursor: isClickable ? 'pointer' : 'default' }}
              title={isClickable ? 'Click to open file' : ''}
            >
              {displayName}
            </span>
            <button
              type="button"
              className="incident-report-delete-file"
              onClick={onDelete}
              aria-label="Delete file"
            >
              &times;
            </button>
          </li>
        </ul>
      </div>
    );
  };

  // Fetch employee data when in edit mode
  useEffect(() => {
    if (isEditing && employeeId) {
      dispatch(fetchEmployeeThunk(employeeId));
    } else {
      dispatch(clearCurrentEmployee());
    }
  }, [dispatch, isEditing, employeeId]);

  // Update form data when employee data is loaded
  useEffect(() => {
    if (currentEmployee) {
      setFormData({
        // User information
        username: currentEmployee.user?.username || '',
        email: currentEmployee.user?.email || '',
        first_name: currentEmployee.user?.first_name || '',
        last_name: currentEmployee.user?.last_name || '',
        
        // Profile information
        image_preview: currentEmployee.image || null,
        father_name: currentEmployee.father_name || '',
        mother_name: currentEmployee.mother_name || '',
        official_email: currentEmployee.official_email || '',
        date_of_birth: currentEmployee.date_of_birth || '',
        education: currentEmployee.education || '',
        aadhaar_number: currentEmployee.aadhaar_number || '',
        pan_number: currentEmployee.pan_number || '',
        contact_no: currentEmployee.contact_no || '',
        current_address: currentEmployee.current_address || '',
        permanent_address: currentEmployee.permanent_address || '',
        emergency_contact_name: currentEmployee.emergency_contact_name || '',
        emergency_contact_relation: currentEmployee.emergency_contact_relation || '',
        emergency_contact_phone: currentEmployee.emergency_contact_phone || '',
        
        // Employment information
        role: currentEmployee.role || '',
        department: currentEmployee.department || '',
        shift: currentEmployee.shift || '',
        date_of_joining: currentEmployee.date_of_joining || '',
        allocated_under: currentEmployee.allocated_under || '',
        biometric_id: currentEmployee.biometric_id || '',
        week_off: currentEmployee.week_off || '',
        
        // Financial information
        epfo_no: currentEmployee.epfo_no || '',
        esic_no: currentEmployee.esic_no || '',
        reference_by: currentEmployee.reference_by || '',
        name_on_passbook: currentEmployee.name_on_passbook || '',
        bank_name: currentEmployee.bank_name || '',
        account_number: currentEmployee.account_number || '',
        ifsc_code: currentEmployee.ifsc_code || '',
        branch_name: currentEmployee.branch_name || '',
        
        // Document uploads
        aadhaar_card_preview: currentEmployee.aadhaar_card || null,
        pan_card_preview: currentEmployee.pan_card || null,
        qualification_document_preview: currentEmployee.qualification_document || null,
        resume_preview: currentEmployee.resume || null,
        employment_document_preview: currentEmployee.employment_document || null,
        bank_passbook_preview: currentEmployee.bank_passbook || null,
        passport_id_preview: currentEmployee.passport_id || null,
        spouse_photo_id_preview: currentEmployee.spouse_photo_id || null,
        pcc_preview: currentEmployee.pcc || null,
        code_of_conduct_preview: currentEmployee.code_of_conduct || null,
        company_assets_preview: currentEmployee.company_assets || null,
      });
    }
  }, [currentEmployee]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file uploads
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      
      setFormData(prev => ({
        ...prev,
        [name]: file,
        [`${name}_preview`]: previewUrl
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    // Required fields validation - only for create mode
    if (!isEditing) {
      if (!formData.first_name) errors.first_name = "First name is required";
      if (!formData.last_name) errors.last_name = "Last name is required";
      if (!formData.email) errors.email = "Email is required";
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    // Phone number validation
    const phoneRegex = /^\d{10}$/;
    if (formData.contact_no && !phoneRegex.test(formData.contact_no)) {
      errors.contact_no = "Please enter a valid 10-digit phone number";
    }
    
    if (formData.emergency_contact_phone && !phoneRegex.test(formData.emergency_contact_phone)) {
      errors.emergency_contact_phone = "Please enter a valid 10-digit phone number";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Download data as CSV
  const downloadAsCSV = () => {
    if (!creationResponse) return;

    const csvData = [];
    const headers = ['Field', 'Value'];
    csvData.push(headers);

    // Add employee information
    csvData.push(['Employee ID', creationResponse.employee_id || 'N/A']);
    csvData.push(['First Name', creationResponse.employee?.user?.first_name || 'N/A']);
    csvData.push(['Last Name', creationResponse.employee?.user?.last_name || 'N/A']);
    csvData.push(['Email', creationResponse.employee?.user?.email || 'N/A']);
    csvData.push(['Role', creationResponse.employee?.role || 'N/A']);
    csvData.push(['Department', creationResponse.employee?.department || 'N/A']);
    csvData.push(['Creation Date', new Date().toLocaleDateString()]);

    // Add credentials
    if (creationResponse.credentials) {
      csvData.push(['', '']); // Empty row for separation
      csvData.push(['=== LOGIN CREDENTIALS ===', '']);
      csvData.push(['Username', creationResponse.credentials.username]);
      csvData.push(['Password', creationResponse.credentials.password]);
      csvData.push(['Email', creationResponse.credentials.email]);
    }

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => 
        typeof field === 'string' && field.includes(',') 
          ? `"${field}"` 
          : field
      ).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `employee_${creationResponse.employee_id}_credentials.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard');
    });
  };

  // Close modal and navigate
  const handleCloseModal = () => {
    setShowConfirmationModal(false);
    setCreationResponse(null);
    navigate('/employees');
  };

  // FIXED: Handle form submission with proper FormData for both creation and editing
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      // Create FormData for proper file upload handling
      const formDataToSend = new FormData();
      
      if (isEditing) {
        // For editing: Structure user data as nested object (as expected by serializer)
        const userUpdates = {};
        if (formData.first_name) userUpdates.first_name = formData.first_name;
        if (formData.last_name) userUpdates.last_name = formData.last_name;
        if (formData.email) {
          userUpdates.email = formData.email;
          // Username and email should be the same according to your backend
          userUpdates.username = formData.email;
        }
        
        // Add the nested user object if there are updates
        if (Object.keys(userUpdates).length > 0) {
          formDataToSend.append('user', JSON.stringify(userUpdates));
        }
      } else {
        // For creation: Send user fields directly (as handled by your view)
        formDataToSend.append('email', formData.email);
        formDataToSend.append('first_name', formData.first_name);
        formDataToSend.append('last_name', formData.last_name);
        // Note: username will be set to email automatically by your backend
      }
      
      // Add text fields (only if they have values)
      const textFields = [
        'father_name', 'mother_name', 'official_email', 'date_of_birth', 
        'education', 'aadhaar_number', 'pan_number', 'contact_no', 
        'current_address', 'permanent_address', 'emergency_contact_name', 
        'emergency_contact_relation', 'emergency_contact_phone', 'role', 'department', 
        'shift', 'date_of_joining', 'biometric_id', 'week_off', 'epfo_no', 
        'esic_no', 'reference_by', 'name_on_passbook', 'bank_name', 
        'account_number', 'ifsc_code', 'branch_name'
      ];
      
      textFields.forEach(field => {
        if (formData[field]) {
          // Handle allocated_under as a foreign key (convert to number if it's a valid number)
          if (field === 'allocated_under') {
            const allocatedValue = formData[field];
            // If it's a number string, convert to integer, otherwise send as string
            const numValue = parseInt(allocatedValue, 10);
            if (!isNaN(numValue) && numValue.toString() === allocatedValue.toString()) {
              formDataToSend.append(field, numValue);
            } else {
              formDataToSend.append(field, allocatedValue);
            }
          } else {
            formDataToSend.append(field, formData[field]);
          }
        }
      });
      
      // Add file uploads (only actual File objects)
      const fileFields = [
        'image', 'aadhaar_card', 'pan_card', 'qualification_document', 
        'resume', 'employment_document', 'bank_passbook', 'passport_id', 
        'spouse_photo_id', 'pcc', 'code_of_conduct', 'company_assets'
      ];
      
      fileFields.forEach(field => {
        if (formData[field] && formData[field] instanceof File) {
          formDataToSend.append(field, formData[field]);
        }
      });
      
      // Debug log to check what's being sent
      console.log('Form submission data:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }
      
      if (isEditing) {
        const response = await dispatch(updateEmployeeThunk({
          employeeId,
          updates: formDataToSend
        })).unwrap();
        alert("Employee updated successfully!");
        navigate('/employees');
      } else {
        const response = await dispatch(createEmployeeThunk(formDataToSend)).unwrap();
        
        // Show modal with creation response
        setCreationResponse(response);
        setShowConfirmationModal(true);
      }
    } catch (err) {
      console.error("Failed to save employee:", err);
      // Show more detailed error information
      if (err.response?.data) {
        console.error("API Error Details:", err.response.data);
        alert(`Error: ${JSON.stringify(err.response.data, null, 2)}`);
      }
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // If loading data in edit mode
  if (loading && isEditing && !currentEmployee) {
    return (
      <div className="incident-report-app">
        <div className="incident-report-content">
          <div className="incident-report-loading">
            <div className="incident-report-loader"></div>
            <p>Loading Employee Data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-form incident-report-app">
      <div className="incident-report-content">
        <div className="incident-report-layout">
          {/* Left side - Image */}
          <div className="incident-report-image-side">
            <div className="incident-report-image-content">
              {/* Content at top if needed */}
            </div>
            {/* Back button at bottom of left panel */}
            <button 
              type="button" 
              className="incident-report-back-btn"
              onClick={handleBack}
            >
              ← Back
            </button>
          </div>
          
          {/* Right side - Form */}
          <div className="incident-report-form-side">
            <div className="incident-report-container">
              <div className="incident-report-header">
                <h1>{isEditing ? 'Edit Employee' : 'Create Employee'}</h1>
              </div>

              {error && (
                <div className="incident-report-section">
                  <div className="incident-report-error-message">
                    {error}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} encType="multipart/form-data">
                {/* Basic Information Section */}
                <div className="incident-report-section">
                  <h2>Basic Information</h2>
                  
                  <div className="incident-report-group">
                    <label htmlFor="first_name">First Name{!isEditing && <span className="required">*</span>}</label>
                    <input
                      id="first_name"
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.first_name ? 'error' : ''}`}
                      placeholder="First Name"
                      required={!isEditing}
                    />
                    {formErrors.first_name && <div className="incident-report-error">{formErrors.first_name}</div>}
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="last_name">Last Name{!isEditing && <span className="required">*</span>}</label>
                    <input
                      id="last_name"
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.last_name ? 'error' : ''}`}
                      placeholder="Last Name"
                      required={!isEditing}
                    />
                    {formErrors.last_name && <div className="incident-report-error">{formErrors.last_name}</div>}
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="email">Email (Username){!isEditing && <span className="required">*</span>}</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.email ? 'error' : ''}`}
                      placeholder="Email Address (will be used as username)"
                      required={!isEditing}
                    />
                    {formErrors.email && <div className="incident-report-error">{formErrors.email}</div>}
                    <div className="incident-report-helper-text">
                      Email address will be used as the username for login
                    </div>
                  </div>
                  
                  {isEditing && (
                    <div className="incident-report-group">
                      <label htmlFor="username">Username</label>
                      <input
                        id="username"
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        className="incident-report-input"
                        placeholder="Username"
                        disabled
                      />
                      <div className="incident-report-helper-text">Username is the same as email and cannot be changed after creation</div>
                    </div>
                  )}
                  
                  <div className="incident-report-group">
                    <label>Profile Image</label>
                    <div className="incident-report-file-container">
                      <label className="incident-report-file-upload">
                        <input
                          type="file"
                          ref={profileImageRef}
                          name="image"
                          onChange={handleFileChange}
                          accept="image/*"
                          className="incident-report-file-input"
                        />
                        <div className="incident-report-upload-content">
                          <div className="incident-report-upload-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                          </div>
                          <div className="incident-report-upload-text">
                            <span>Upload profile image</span>
                            <p>Click to browse or drag file here</p>
                          </div>
                        </div>
                      </label>
                    </div>
                    {renderFilePreview(
                      formData.image_preview, 
                      'Profile Image',
                      () => setFormData(prev => ({...prev, image: null, image_preview: null})),
                      true
                    )}
                  </div>
                </div>

                {/* Personal Information Section */}
                <div className="incident-report-section">
                  <h2>Personal Information</h2>
                  
                  <div className="incident-report-group">
                    <label htmlFor="father_name">Father's Name</label>
                    <input
                      id="father_name"
                      type="text"
                      name="father_name"
                      value={formData.father_name}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Father's Name"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="mother_name">Mother's Name</label>
                    <input
                      id="mother_name"
                      type="text"
                      name="mother_name"
                      value={formData.mother_name}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Mother's Name"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="date_of_birth">Date of Birth</label>
                    <input
                      id="date_of_birth"
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className="incident-report-date"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="aadhaar_number">Aadhaar Number</label>
                    <input
                      id="aadhaar_number"
                      type="text"
                      name="aadhaar_number"
                      value={formData.aadhaar_number}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Aadhaar Number"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="pan_number">PAN Number</label>
                    <input
                      id="pan_number"
                      type="text"
                      name="pan_number"
                      value={formData.pan_number}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="PAN Number"
                    />
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="incident-report-section">
                  <h2>Contact Information</h2>
                  
                  <div className="incident-report-group">
                    <label htmlFor="contact_no">Contact Number</label>
                    <input
                      id="contact_no"
                      type="text"
                      name="contact_no"
                      value={formData.contact_no}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.contact_no ? 'error' : ''}`}
                      placeholder="Contact Number"
                    />
                    {formErrors.contact_no && <div className="incident-report-error">{formErrors.contact_no}</div>}
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="official_email">Official Email</label>
                    <input
                      id="official_email"
                      type="email"
                      name="official_email"
                      value={formData.official_email}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Official Email"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="current_address">Current Address</label>
                    <textarea
                      id="current_address"
                      name="current_address"
                      value={formData.current_address}
                      onChange={handleChange}
                      className="incident-report-textarea"
                      placeholder="Current Address"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="permanent_address">Permanent Address</label>
                    <textarea
                      id="permanent_address"
                      name="permanent_address"
                      value={formData.permanent_address}
                      onChange={handleChange}
                      className="incident-report-textarea"
                      placeholder="Permanent Address"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="emergency_contact_name">Emergency Contact Name</label>
                    <input
                      id="emergency_contact_name"
                      type="text"
                      name="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Emergency Contact Name"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="emergency_contact_relation">Relation</label>
                    <input
                      id="emergency_contact_relation"
                      type="text"
                      name="emergency_contact_relation"
                      value={formData.emergency_contact_relation}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Relation"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="emergency_contact_phone">Emergency Contact Phone</label>
                    <input
                      id="emergency_contact_phone"
                      type="text"
                      name="emergency_contact_phone"
                      value={formData.emergency_contact_phone}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.emergency_contact_phone ? 'error' : ''}`}
                      placeholder="Emergency Contact Phone"
                    />
                    {formErrors.emergency_contact_phone && <div className="incident-report-error">{formErrors.emergency_contact_phone}</div>}
                  </div>
                </div>

                {/* Employment Details Section */}
                <div className="incident-report-section">
                  <h2>Employment Details</h2>
                  
            <div className="incident-report-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="incident-report-input"
                required
              >
                <option value="">Select Role</option>
                
                {/* Global Admin */}
                <option value="ADMIN">Admin</option>
                
                {/* Operations Department */}
                <optgroup label="Operations">
                  <option value="OPS_HEAD_ADMIN">Ops Head Admin</option>
                  <option value="MANAGER">Manager</option>
                  <option value="TEAM_MANAGER">Team Manager</option>
                </optgroup>
                
                {/* HR Department */}
                <optgroup label="HR Department">
                  <option value="HR_HEAD">HR Head</option>
                  <option value="HR_MANAGER">HR Manager</option>
                  <option value="HR_COORDINATOR">HR Coordinator</option>
                  <option value="PAYROLL_ASSISTANT">Payroll Assistant</option>
                </optgroup>
                
                {/* Sales Department */}
                <optgroup label="Sales">
                  <option value="SALES_HEAD">Sales Head</option>
                  <option value="SALES_EXECUTIVE">Sales Executive</option>
                  <option value="SALES_ASSISTANT">Sales Assistant</option>
                </optgroup>
                
                {/* Tech Backend */}
                <optgroup label="Tech Backend">
                  <option value="TECH_ADMIN">Tech Admin</option>
                  <option value="TECH_MANAGER">Tech Manager</option>
                  <option value="TECH_EXECUTIVE">Tech Executive</option>
                  <option value="TECH_ASSISTANT">Tech Assistant</option>
                </optgroup>
                
                {/* Development Team */}
                <optgroup label="Development Team">
                  <option value="CHIEF_DEVELOPMENT_HEAD">Chief Development Head</option>
                  <option value="DEVELOPMENT_MANAGER">Development Manager</option>
                  <option value="FRONTEND_BACKEND_EXECUTIVE">Frontend/Backend Executive</option>
                  <option value="FRONTEND_BACKEND_ASSISTANT">Frontend/Backend Assistant</option>
                </optgroup>
                
                {/* Floor Support */}
                <optgroup label="Floor Support">
                  <option value="FL_ADMIN">FL Admin</option>
                  <option value="FLOOR_SUPERVISOR">Floor Supervisor</option>
                  <option value="FLOOR_COORDINATOR">Floor Co-ordinator</option>
                  <option value="OFFICE_ASSISTANT">Office Assistant</option>
                </optgroup>
                
                {/* Common Roles */}
                <optgroup label="Common Roles">
                  <option value="SPA">Senior Process Associate</option>
                  <option value="PA">Process Associate</option>
                </optgroup>
              </select>
            </div>
                  
            <div className="incident-report-group">
  <label htmlFor="department">Department *</label>
  <select
    id="department"
    name="department"
    value={formData.department}
    onChange={handleChange}
    className="incident-report-input"
    required
  >
    <option value="">Select Department</option>
    <option value="OPERATIONS">Operations</option>
    <option value="HR">HR Department</option>
    <option value="SALES">Sales</option>
    <option value="TECH_BACKEND">Tech Backend</option>
    <option value="DEVELOPMENT">Development Team</option>
    <option value="FLOOR_SUPPORT">Floor Support</option>
  </select>
</div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="shift">Shift</label>
                    <input
                      id="shift"
                      type="text"
                      name="shift"
                      value={formData.shift}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Shift"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="date_of_joining">Date of Joining</label>
                    <input
                      id="date_of_joining"
                      type="date"
                      name="date_of_joining"
                      value={formData.date_of_joining}
                      onChange={handleChange}
                      className="incident-report-date"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="allocated_under">Allocated Under</label>
                    <input
                      id="allocated_under"
                      type="text"
                      name="allocated_under"
                      value={formData.allocated_under}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Allocated Under (Enter Manager ID or Name)"
                    />
                    <div className="incident-report-helper-text">
                      Enter the manager's ID number if assigning to a specific manager
                    </div>
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="biometric_id">Biometric ID</label>
                    <input
                      id="biometric_id"
                      type="text"
                      name="biometric_id"
                      value={formData.biometric_id}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Biometric ID"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="week_off">Week Off</label>
                    <input
                      id="week_off"
                      type="text"
                      name="week_off"
                      value={formData.week_off}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Week Off (e.g., Sunday)"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="education">Education</label>
                    <input
                      id="education"
                      type="text"
                      name="education"
                      value={formData.education}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Highest Education"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="reference_by">Reference By</label>
                    <input
                      id="reference_by"
                      type="text"
                      name="reference_by"
                      value={formData.reference_by}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Reference By"
                    />
                  </div>
                </div>

                {/* Financial Information Section */}
                <div className="incident-report-section">
                  <h2>Financial Information</h2>
                  
                  <div className="incident-report-group">
                    <label htmlFor="epfo_no">EPFO Number</label>
                    <input
                      id="epfo_no"
                      type="text"
                      name="epfo_no"
                      value={formData.epfo_no}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="EPFO Number"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="esic_no">ESIC Number</label>
                    <input
                      id="esic_no"
                      type="text"
                      name="esic_no"
                      value={formData.esic_no}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="ESIC Number"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="name_on_passbook">Name on Passbook</label>
                    <input
                      id="name_on_passbook"
                      type="text"
                      name="name_on_passbook"
                      value={formData.name_on_passbook}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Name as on Bank Passbook"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="bank_name">Bank Name</label>
                    <input
                      id="bank_name"
                      type="text"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Bank Name"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="account_number">Account Number</label>
                    <input
                      id="account_number"
                      type="text"
                      name="account_number"
                      value={formData.account_number}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Account Number"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="ifsc_code">IFSC Code</label>
                    <input
                      id="ifsc_code"
                      type="text"
                      name="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="IFSC Code"
                    />
                  </div>
                  
                  <div className="incident-report-group">
                    <label htmlFor="branch_name">Branch Name</label>
                    <input
                      id="branch_name"
                      type="text"
                      name="branch_name"
                      value={formData.branch_name}
                      onChange={handleChange}
                      className="incident-report-input"
                      placeholder="Branch Name"
                    />
                  </div>
                </div>

                {/* Document Uploads Section */}
                <div className="incident-report-section">
                  <h2>Document Uploads</h2>
                  
                  <div className="incident-report-file-list">
                    <h3>Upload Required Documents</h3>
                    
                    <div className="incident-report-document-grid">
                      {/* Aadhaar Card Upload */}
                      <div className="incident-report-document-item">
                        <label>Aadhaar Card</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={aadhaarCardRef}
                              name="aadhaar_card"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.aadhaar_card_preview, 
                          'Aadhaar Card',
                          () => setFormData(prev => ({...prev, aadhaar_card: null, aadhaar_card_preview: null})),
                          true
                        )}
                      </div>
                      
                      {/* PAN Card Upload */}
                      <div className="incident-report-document-item">
                        <label>PAN Card</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={panCardRef}
                              name="pan_card"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.pan_card_preview, 
                          'PAN Card',
                          () => setFormData(prev => ({...prev, pan_card: null, pan_card_preview: null})),
                          true
                        )}
                      </div>
                      
                      {/* Resume Upload - Updated to accept images too */}
                      <div className="incident-report-document-item">
                        <label>Resume</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={resumeRef}
                              name="resume"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.resume_preview, 
                          'Resume',
                          () => setFormData(prev => ({...prev, resume: null, resume_preview: null})),
                          true
                        )}
                      </div>
                      
                      {/* Qualification Document Upload */}
                      <div className="incident-report-document-item">
                        <label>Qualification Document</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={qualificationDocRef}
                              name="qualification_document"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.qualification_document_preview, 
                          'Qualification Document',
                          () => setFormData(prev => ({...prev, qualification_document: null, qualification_document_preview: null})),
                          true
                        )}
                      </div>
                      
                      {/* Bank Passbook Upload */}
                      <div className="incident-report-document-item">
                        <label>Bank Passbook</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={bankPassbookRef}
                              name="bank_passbook"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.bank_passbook_preview, 
                          'Bank Passbook',
                          () => setFormData(prev => ({...prev, bank_passbook: null, bank_passbook_preview: null})),
                          true
                        )}
                      </div>
                      
                      {/* Employment Document Upload */}
                      <div className="incident-report-document-item">
                        <label>Employment Document</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={employmentDocRef}
                              name="employment_document"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.employment_document_preview, 
                          'Employment Document',
                          () => setFormData(prev => ({...prev, employment_document: null, employment_document_preview: null})),
                          true
                        )}
                      </div>

                      {/* Additional document fields */}
                      <div className="incident-report-document-item">
                        <label>Passport ID</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={passportIdRef}
                              name="passport_id"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.passport_id_preview, 
                          'Passport ID',
                          () => setFormData(prev => ({...prev, passport_id: null, passport_id_preview: null})),
                          true
                        )}
                      </div>

                      <div className="incident-report-document-item">
                        <label>Spouse Photo ID</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={spousePhotoIdRef}
                              name="spouse_photo_id"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.spouse_photo_id_preview, 
                          'Spouse Photo ID',
                          () => setFormData(prev => ({...prev, spouse_photo_id: null, spouse_photo_id_preview: null})),
                          true
                        )}
                      </div>

                      <div className="incident-report-document-item">
                        <label>PCC</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={pccRef}
                              name="pcc"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.pcc_preview, 
                          'PCC',
                          () => setFormData(prev => ({...prev, pcc: null, pcc_preview: null})),
                          true
                        )}
                      </div>

                      <div className="incident-report-document-item">
                        <label>Code of Conduct</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={codeOfConductRef}
                              name="code_of_conduct"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.code_of_conduct_preview, 
                          'Code of Conduct',
                          () => setFormData(prev => ({...prev, code_of_conduct: null, code_of_conduct_preview: null})),
                          true
                        )}
                      </div>

                      <div className="incident-report-document-item">
                        <label>Company Assets</label>
                        <div className="incident-report-file-container small">
                          <label className="incident-report-file-upload">
                            <input
                              type="file"
                              ref={companyAssetsRef}
                              name="company_assets"
                              onChange={handleFileChange}
                              className="incident-report-file-input"
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <div className="incident-report-upload-content small">
                              <div className="incident-report-upload-icon small">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text small">
                                <span>Upload</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        {renderFilePreview(
                          formData.company_assets_preview, 
                          'Company Assets',
                          () => setFormData(prev => ({...prev, company_assets: null, company_assets_preview: null})),
                          true
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="incident-report-actions">
                  <button 
                    type="button" 
                    className="incident-report-btn-secondary"
                    onClick={handleBack}
                  >
                    Cancel
                  </button>
                  {userIsAdmin && (
                    <div className="incident-report-right-actions">
                      <button 
                        type="submit" 
                        className="incident-report-btn-primary"
                        disabled={loading}
                      >
                        {loading ? 'Processing...' : isEditing ? 'Update Employee' : 'Create Employee'}
                      </button>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Creation Confirmation Modal */}
      {showConfirmationModal && creationResponse && (
        <div className="emp-mgmt-modal-overlay">
          <div className="emp-mgmt-modal">
            <div className="emp-mgmt-modal-header">
              <h2>🎉 Employee Created Successfully!</h2>
              <button 
                className="emp-mgmt-modal-close"
                onClick={handleCloseModal}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <div className="emp-mgmt-modal-content">
              {/* Employee Information */}
              <div className="emp-mgmt-info-section">
                <h3>Employee Information</h3>
                <div className="emp-mgmt-info-grid">
                  <div className="emp-mgmt-info-item">
                    <span className="emp-mgmt-info-label">Employee ID:</span>
                    <span className="emp-mgmt-info-value">
                      {creationResponse.employee_id}
                      <button 
                        className="emp-mgmt-copy-btn"
                        onClick={() => copyToClipboard(creationResponse.employee_id?.toString())}
                        title="Copy Employee ID"
                      >
                        📋
                      </button>
                    </span>
                  </div>
                  <div className="emp-mgmt-info-item">
                    <span className="emp-mgmt-info-label">Name:</span>
                    <span className="emp-mgmt-info-value">
                      {creationResponse.employee?.user?.first_name} {creationResponse.employee?.user?.last_name}
                    </span>
                  </div>
                  <div className="emp-mgmt-info-item">
                    <span className="emp-mgmt-info-label">Email:</span>
                    <span className="emp-mgmt-info-value">{creationResponse.employee?.user?.email}</span>
                  </div>
                  <div className="emp-mgmt-info-item">
                    <span className="emp-mgmt-info-label">Role:</span>
                    <span className="emp-mgmt-info-value">{creationResponse.employee?.role || 'Not Specified'}</span>
                  </div>
                  <div className="emp-mgmt-info-row">
                    <span className="emp-mgmt-info-label">Department:</span>
                    <span className="emp-mgmt-info-value">{creationResponse.employee?.department || 'Not Specified'}</span>
                  </div>
                </div>
              </div>

              {/* Login Credentials */}
              {creationResponse.credentials && (
                <div className="emp-mgmt-info-section emp-mgmt-credentials-section">
                  <h3>🔐 Employee Login Credentials</h3>
                  <div className="emp-mgmt-credentials-warning">
                    <span className="emp-mgmt-warning-icon">⚠️</span>
                    <span>Please save these credentials securely. They won't be shown again!</span>
                  </div>
                  <div className="emp-mgmt-info-grid">
                    <div className="emp-mgmt-info-item">
                      <span className="emp-mgmt-info-label">Username (Email):</span>
                      <span className="emp-mgmt-info-value emp-mgmt-credential-value">
                        {creationResponse.credentials.username}
                        <button 
                          className="emp-mgmt-copy-btn"
                          onClick={() => copyToClipboard(creationResponse.credentials.username)}
                          title="Copy Username"
                        >
                          📋
                        </button>
                      </span>
                    </div>
                    <div className="emp-mgmt-info-item">
                      <span className="emp-mgmt-info-label">Password:</span>
                      <span className="emp-mgmt-info-value emp-mgmt-credential-value">
                        {creationResponse.credentials.password}
                        <button 
                          className="emp-mgmt-copy-btn"
                          onClick={() => copyToClipboard(creationResponse.credentials.password)}
                          title="Copy Password"
                        >
                          📋
                        </button>
                      </span>
                    </div>
                    <div className="emp-mgmt-info-item">
                      <span className="emp-mgmt-info-label">Email:</span>
                      <span className="emp-mgmt-info-value">{creationResponse.credentials.email}</span>
                    </div>
                  </div>
                  <div className="emp-mgmt-credentials-note">
                    <p>Note: The username and email are the same. The employee can login using their email address.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="emp-mgmt-modal-actions">
              <button 
                className="emp-mgmt-btn-download"
                onClick={downloadAsCSV}
                type="button"
              >
                📥 Download CSV
              </button>
              <button 
                className="emp-mgmt-btn-close"
                onClick={handleCloseModal}
                type="button"
              >
                Close & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Management Modal Styles */}
      <style jsx>{`
        .incident-report-helper-text {
          font-size: 0.875rem;
          color: #6b7280;
          margin-top: 4px;
          line-height: 1.4;
        }

        /* File Icon Styles */
        .incident-report-file-icon {
          width: 80px;
          height: 80px;
          background: #f3f4f6;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          transition: all 0.2s;
        }

        .incident-report-file-icon:hover {
          background: #e5e7eb;
          border-color: #d1d5db;
          color: #374151;
        }

        .incident-report-file-name {
          flex: 1;
          margin-left: 12px;
          font-size: 0.875rem;
          color: #374151;
          word-break: break-word;
          transition: color 0.2s;
        }

        .incident-report-file-name:hover {
          color: #1f2937;
          text-decoration: underline;
        }

        /* Employee Management Modal Styles */
        .emp-mgmt-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        }

        .emp-mgmt-modal {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .emp-mgmt-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 32px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border-radius: 12px 12px 0 0;
        }

        .emp-mgmt-modal-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .emp-mgmt-modal-close {
          background: none;
          border: none;
          font-size: 2rem;
          color: white;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
        }

        .emp-mgmt-modal-close:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        .emp-mgmt-modal-content {
          padding: 32px;
        }

        .emp-mgmt-info-section {
          margin-bottom: 32px;
        }

        .emp-mgmt-info-section:last-child {
          margin-bottom: 0;
        }

        .emp-mgmt-info-section h3 {
          margin: 0 0 16px 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .emp-mgmt-credentials-section {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          padding: 20px;
          border-radius: 8px;
          border: 2px solid #10b981;
        }

        .emp-mgmt-credentials-section h3 {
          color: #065f46;
        }

        .emp-mgmt-credentials-warning {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding: 12px;
          background: #fef3c7;
          border-radius: 6px;
          color: #92400e;
          font-weight: 500;
          font-size: 0.875rem;
        }

        .emp-mgmt-credentials-note {
          margin-top: 16px;
          padding: 12px;
          background: #f0f9ff;
          border-radius: 6px;
          border-left: 4px solid #0ea5e9;
        }

        .emp-mgmt-credentials-note p {
          margin: 0;
          color: #0c4a6e;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .emp-mgmt-warning-icon {
          font-size: 1.2rem;
        }

        .emp-mgmt-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px;
        }

        .emp-mgmt-info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .emp-mgmt-info-label {
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .emp-mgmt-info-value {
          font-size: 1rem;
          color: #111827;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #f9fafb;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .emp-mgmt-credential-value {
          background: #f0fdf4;
          border-color: #bbf7d0;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-weight: 600;
        }

        .emp-mgmt-copy-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
          font-size: 1rem;
        }

        .emp-mgmt-copy-btn:hover {
          background-color: #e5e7eb;
        }

        .emp-mgmt-modal-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 32px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 0 0 12px 12px;
        }

        .emp-mgmt-btn-download {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .emp-mgmt-btn-download:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .emp-mgmt-btn-close {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .emp-mgmt-btn-close:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        @media (max-width: 768px) {
          .emp-mgmt-modal {
            margin: 20px;
            max-height: calc(100vh - 40px);
          }

          .emp-mgmt-modal-header,
          .emp-mgmt-modal-content,
          .emp-mgmt-modal-actions {
            padding: 20px;
          }

          .emp-mgmt-info-grid {
            grid-template-columns: 1fr;
          }

          .emp-mgmt-modal-actions {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .emp-mgmt-btn-download,
          .emp-mgmt-btn-close {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default EmployeeManagementForm;